import React from 'react';

import styles from './SyncButton.module.scss';

export interface SyncButtonProps {
  /** Whether the sync operation is currently in progress */
  loading: boolean;
  
  /** Click handler for the sync button */
  onClick: () => void;
  
  /** Whether the button should be disabled */
  disabled?: boolean;
  
  /** Custom button text (default: "Sync") */
  text?: string;
  
  /** Loading text to display when syncing (default: "Syncing...") */
  loadingText?: string;
  
  /** Optional CSS class name for custom styling */
  className?: string;
  
  /** Optional ARIA label override */
  ariaLabel?: string;
}

/**
 * SyncButton - A reusable button component for triggering sync operations
 * 
 * @example
 * ```tsx
 * <SyncButton
 *   loading={syncing}
 *   onClick={handleSync}
 *   disabled={syncInProgress}
 *   text="Sync Games"
 * />
 * ```
 * 
 * @param {SyncButtonProps} props - Component props
 * @returns {JSX.Element} Rendered sync button
 */
const SyncButton: React.FC<SyncButtonProps> = ({
  loading,
  onClick,
  disabled = false,
  text = 'Sync',
  loadingText = 'Syncing...',
  className = '',
  ariaLabel,
}) => {
  const isDisabled = disabled || loading;
  const displayText = loading ? loadingText : text;
  const buttonAriaLabel = ariaLabel || displayText;

  return (
    <button
      className={`${styles.syncButton} ${className}`}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={buttonAriaLabel}
      aria-busy={loading}
      type="button"
    >
      {loading && (
        <span className={styles.syncIcon} aria-hidden="true">
          ⟳
        </span>
      )}
      <span>{displayText}</span>
    </button>
  );
};

export default SyncButton;
